import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@carepass/api-client";
import { memberSessionStore } from "../../app/runtime";
import { authApi } from "../shared/api";
import { Alert, Button, Surface } from "../shared/ui";
import { SignupPasswordStep } from "../shared/SignupPinKeypad";
import { clearPasswordChangedSession } from "./password-session";

export function PasswordResetSteps({ verificationToken, onStepChange, onPendingChange, onFinishing, onCompleted, onRestart }: {
  readonly verificationToken: string;
  readonly onStepChange: (step: "new" | "confirmation") => void;
  readonly onPendingChange: (value: boolean) => void;
  readonly onFinishing: () => void;
  readonly onCompleted: () => void;
  readonly onRestart: (reason?: string) => Promise<void>;
}) {
  const client = useQueryClient();
  const [step, setStep] = useState<"new" | "confirmation">("new");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [pending, setPending] = useState(false);
  const requestPending = useRef(false);
  const mounted = useRef(false);
  const generation = useRef(0);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; generation.current += 1; }; }, []);
  const [error, setError] = useState<string>();
  const [restartRequired, setRestartRequired] = useState(false);
  const restartReason = useRef<string | undefined>(undefined);
  const restart = async () => {
    try { await onRestart(restartReason.current); }
    catch { if (mounted.current) setError("이전 본인인증을 종료하지 못했습니다. 본인인증 다시 시작을 눌러 재시도해 주세요."); }
  };
  const complete = async () => {
    if (requestPending.current || confirmation.length !== 6) return;
    if (password !== confirmation) { setConfirmation(""); setError("비밀번호가 서로 다릅니다. 새 비밀번호를 다시 확인해 주세요."); return; }
    requestPending.current = true; setPending(true); onPendingChange(true); setError(undefined);
    const visit = generation.current;
    const sessionAtStart = memberSessionStore.get();
    const present = () => mounted.current && generation.current === visit;
    try {
      await authApi.resetPassword(verificationToken, { password, passwordConfirmation: confirmation });
      if (present()) { setPassword(""); setConfirmation(""); onFinishing(); }
      // The caller finishes its identity proof before clearing the authenticated member session.
      const cleared = await clearPasswordChangedSession(client, sessionAtStart);
      if (present() && cleared) onCompleted();
    } catch (cause) {
      if (!present()) return;
      if (cause instanceof ApiError && ["PASSWORD_RESET_INVALID", "MEMBER_IDENTITY_MISMATCH"].includes(cause.code ?? "")) {
        restartReason.current = cause.code === "MEMBER_IDENTITY_MISMATCH" ? cause.message : undefined;
        setPassword(""); setConfirmation(""); setRestartRequired(true); await restart();
      } else if (cause instanceof ApiError && cause.code === "MEMBER_PASSWORD_MISMATCH") { setConfirmation(""); setError(cause.message); }
      else if (cause instanceof ApiError && cause.code === "MEMBER_PASSWORD_INVALID") { setPassword(""); setConfirmation(""); setStep("new"); onStepChange("new"); setError(cause.message); }
      else setError(cause instanceof ApiError ? cause.message : "비밀번호를 변경하지 못했습니다. 다시 시도해 주세요.");
    } finally { requestPending.current = false; if (present()) { setPending(false); onPendingChange(false); } }
  };
  return <Surface key={step} title={step === "new" ? "2. 새 비밀번호 등록" : "3. 새 비밀번호 재확인"} description="반복·연속되지 않은 숫자 6자리를 사용해 주세요.">
    {error && <Alert persistent variant="danger" title="확인해 주세요">{error}</Alert>}
    <SignupPasswordStep key={step} value={step === "new" ? password : confirmation} onChange={step === "new" ? setPassword : setConfirmation} confirmation={step === "confirmation"} disabled={pending || restartRequired} />
    {restartRequired ? <Button isLoading={pending} onClick={async () => { if (requestPending.current) return; requestPending.current = true; setPending(true); onPendingChange(true); await restart(); requestPending.current = false; if (mounted.current) { setPending(false); onPendingChange(false); } }}>본인인증 다시 시작</Button> : <Button key={`reset-action-${step}`} size="large" isLoading={pending} disabled={(step === "new" ? password : confirmation).length !== 6} onClick={() => {
      if (step === "new") { setError(undefined); setConfirmation(""); setStep("confirmation"); onStepChange("confirmation"); }
      else void complete();
    }}>{step === "new" ? "다음" : "비밀번호 변경"}</Button>}
  </Surface>;
}
